import { Box } from '@wix/design-system';
import React, { useContext, useState } from 'react';

import { RicosContext } from './../../ricos-context';

import { LinkInput } from '../link-input/link-input';
import { CheckboxWithInfoIcon } from './checkbox-with-info-icon';
import {
  LINK_PANEL_MODAL_LINK_INPUT,
  LINK_SETTINGS_CHECKBOX_NEW_TAB,
  LINK_SETTINGS_CHECKBOX_NOFOLLOW,
  LINK_SETTINGS_CHECKBOX_NOREFERRER,
  LINK_SETTINGS_CHECKBOX_SPONSORED,
} from './data-hooks';

export type LinkSettingsProps = {
  linkValue: LinkSettingsValue;
  onChange: (value: LinkSettingsValue, status?: 'OK' | 'ERROR') => void;
  options?: {
    showInputField?: boolean;
    showNewTabCheckbox?: boolean;
    showNoReferrerCheckbox?: boolean;
    showNoFollowCheckbox?: boolean;
    showSponsoredCheckbox?: boolean;
  };
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
};

export type LinkSettingsValue = {
  url: string | undefined;
  openInNewTab?: boolean;
  noreferrer?: boolean;
  nofollow?: boolean;
  sponsored?: boolean;
};

export const LinkSettings: React.FC<LinkSettingsProps> = ({
  linkValue,
  onChange,
  options,
  inputRef,
}) => {
  const { t } = useContext(RicosContext);
  const [state, setState] = useState<LinkSettingsValue>({
    url: linkValue.url || '',
    openInNewTab: !!linkValue.openInNewTab,
    noreferrer: !!linkValue.noreferrer,
    nofollow: !!linkValue.nofollow,
    sponsored: !!linkValue.sponsored,
  });

  const handleCheckboxChange = (key: keyof LinkSettingsValue) => {
    setState(prevState => {
      const newState = { ...prevState, [key]: !prevState[key] };
      if (newState.url) {
        onChange(newState);
      }
      return newState;
    });
  };

  return (
    <Box direction="vertical" gap="SP3" width="100%">
      {options?.showInputField !== false ? (
        <LinkInput
          value={state.url ?? ''}
          onChange={url => {
            setState(prev => {
              const newState = { ...prev, url };
              return newState;
            });
          }}
          onBlur={({ url }, status) => {
            onChange({ ...state, url }, status);
          }}
          placeholder={t('LinkPanel_InputPlaceholder')}
          dataHook={LINK_PANEL_MODAL_LINK_INPUT}
          inputRef={inputRef}
          allowTelLink
        />
      ) : null}
      <Box direction="vertical" gap="SP2">
        {options?.showNewTabCheckbox && (
          <CheckboxWithInfoIcon
            dataHook={LINK_SETTINGS_CHECKBOX_NEW_TAB}
            checked={state.openInNewTab}
            onChange={() => handleCheckboxChange('openInNewTab')}
            textContent="LinkPanel_Target_Checkbox"
            tooltipContent={null}
          />
        )}
        {options?.showNoReferrerCheckbox && (
          <CheckboxWithInfoIcon
            dataHook={LINK_SETTINGS_CHECKBOX_NOREFERRER}
            checked={state.noreferrer}
            onChange={() => handleCheckboxChange('noreferrer')}
            textContent="LinkPanel_Noreferrer_Checkbox_Label"
            tooltipContent="LinkPanel_Noreferrer_Tooltip"
          />
        )}
        {options?.showNoFollowCheckbox && (
          <CheckboxWithInfoIcon
            dataHook={LINK_SETTINGS_CHECKBOX_NOFOLLOW}
            checked={state.nofollow}
            onChange={() => handleCheckboxChange('nofollow')}
            textContent="LinkPanel_Nofollow_Checkbox"
            tooltipContent="LinkPanel_Nofollow_Tooltip"
          />
        )}
        {options?.showSponsoredCheckbox && (
          <CheckboxWithInfoIcon
            dataHook={LINK_SETTINGS_CHECKBOX_SPONSORED}
            checked={state.sponsored}
            onChange={() => handleCheckboxChange('sponsored')}
            textContent="LinkPanel_Sponsored_Checkbox"
            tooltipContent={null}
          />
        )}
      </Box>
    </Box>
  );
};
